import React, { useEffect } from "react";
import { WidgetWrapper } from "../../../../components/WidgetWrapper/WidgetWrapper";
import { EditField } from "../../../../components/EditPanel/types";
import { useWidgetEditable } from "../../../../hooks/useWidgetEditable";
import { themeColor } from "../../../../types/color";
import { FONT_WEIGHT_OPTIONS_KEYWORDS } from "../../../../utils/fontWeightOptions";
import { ConciergeSidebarSkeleton } from "../ConciergeHome/ConciergeSkeletons";
import {
  ConciergeSidebarWidgetState,
  ConciergeSidebarResolvedIcons,
  ConciergeSidebarWidgetProps,
} from "./types";

const loadGoogleFontInHead = (fontUrl: string): void => {
  const existingLink = document.querySelector(`link[href="${fontUrl}"]`);
  if (existingLink) return;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = fontUrl;
  document.head.appendChild(link);
};

// ---------------------------------------------------------------------------
// Default state
// ---------------------------------------------------------------------------

const defaultState: ConciergeSidebarWidgetState = {
  // Logo / Brand Icon
  logoIcon: "default",
  logoIconSize: 24,

  // Brand Link
  brandLinkText: "example.com",
  brandLinkPrefix: "Visit ",
  brandLinkFontSize: 14,
  brandLinkColor: themeColor("textPrimary"),

  // Layout
  expandedWidth: 248,
  collapsedWidth: 48,
  expandedBgColor: themeColor("background"),
  collapsedBgColor: themeColor("secondary"),
  borderColor: themeColor("borderColor"),

  // Navigation Icons
  sidebarIconSize: 24,
  sidebarIconColor: themeColor("textPrimary"),
  composeIconSize: 20,
  composeIconColor: themeColor("textPrimary"),
  closeIconColor: themeColor("textPrimary"),
  chatsIconSize: 20,
  chatsIconColor: themeColor("textPrimary"),

  // Labels
  openSidebarLabel: "Open sidebar",
  closeSidebarLabel: "Close sidebar",
  newChatLabel: "New chat",
  chatsLabel: "Chats",
  signOutLabel: "Sign out",
  labelFontSize: 14,
  labelFontWeight: "medium",
  labelColor: themeColor("textPrimary"),

  // Tooltips
  tooltipBgColor: themeColor("textPrimary"),
  tooltipTextColor: themeColor("secondary"),
  tooltipFontSize: 12,
  tooltipBorderRadius: 8,

  // Chat History
  chatHistoryTitle: "Chat history",
  chatHistoryTitleFontSize: 14,
  chatHistoryTitleFontWeight: "medium",
  chatHistoryTitleColor: themeColor("textPrimary"),
  chatItemFontSize: 14,
  chatItemFontWeight: "medium",
  chatItemColor: themeColor("textPrimary"),
  chatItemActiveBgColor: themeColor("background"),
  chatItemHoverBgColor: themeColor("background"),
  chatItemPaddingX: 8,
  chatItemPaddingY: 4,
  chatItemBorderRadius: 5,
  chatItemLineClamp: 1,

  // Typography
  fontFamily: "inherit",

  // Avatar
  avatarSize: 32,
  avatarBgColor: themeColor("textPrimary"),
  avatarTextColor: themeColor("secondary"),
  avatarFontSize: 12.8,
  avatarFontWeight: "semibold",
};

// ---------------------------------------------------------------------------
// Edit fields (grouped for the edit panel)
// ---------------------------------------------------------------------------

const FONT_WEIGHT_OPTIONS_SHORT = FONT_WEIGHT_OPTIONS_KEYWORDS.slice(0, 4);

const editFields: EditField[] = [
  // ── Logo / Brand Icon ──────────────────────────────────────────────────
  { key: "logoIcon", label: "Logo Icon", type: "icon", group: "Logo" },
  {
    key: "logoIconSize",
    label: "Icon Size",
    type: "number",
    min: 0,
    max: 100,
    step: 1,
    group: "Logo",
  },

  // ── Brand Link ─────────────────────────────────────────────────────────
  {
    key: "brandLinkPrefix",
    label: "Prefix Text",
    type: "text",
    group: "Brand Link",
  },
  {
    key: "brandLinkFontSize",
    label: "Font Size",
    type: "number",
    min: 10,
    max: 18,
    step: 1,
    group: "Brand Link",
  },
  {
    key: "brandLinkColor",
    label: "Color",
    type: "color",
    group: "Brand Link",
  },

  // ── Layout ─────────────────────────────────────────────────────────────
  {
    key: "expandedWidth",
    label: "Expanded Width",
    type: "number",
    min: 200,
    max: 320,
    step: 4,
    group: "Layout",
  },
  {
    key: "collapsedWidth",
    label: "Collapsed Width",
    type: "number",
    min: 36,
    max: 56,
    step: 2,
    group: "Layout",
  },
  {
    key: "expandedBgColor",
    label: "Expanded Background",
    type: "color",
    group: "Layout",
  },
  {
    key: "collapsedBgColor",
    label: "Collapsed Background",
    type: "color",
    group: "Layout",
  },
  {
    key: "borderColor",
    label: "Border Color",
    type: "color",
    group: "Layout",
  },

  // ── Navigation Icons ───────────────────────────────────────────────────
  {
    key: "sidebarIconSize",
    label: "Sidebar Icon Size",
    type: "number",
    min: 16,
    max: 28,
    step: 1,
    group: "Icons",
  },
  {
    key: "sidebarIconColor",
    label: "Sidebar Icon Color",
    type: "color",
    group: "Icons",
  },
  {
    key: "composeIconSize",
    label: "Compose Icon Size",
    type: "number",
    min: 14,
    max: 28,
    step: 1,
    group: "Icons",
  },
  {
    key: "composeIconColor",
    label: "Compose Icon Color",
    type: "color",
    group: "Icons",
  },
  {
    key: "closeIconColor",
    label: "Close Icon Color",
    type: "color",
    group: "Icons",
  },
  {
    key: "chatsIconSize",
    label: "Chats Icon Size",
    type: "number",
    min: 14,
    max: 28,
    step: 1,
    group: "Icons",
  },
  {
    key: "chatsIconColor",
    label: "Chats Icon Color",
    type: "color",
    group: "Icons",
  },

  // ── Labels ─────────────────────────────────────────────────────────────
  {
    key: "openSidebarLabel",
    label: "Open Sidebar Label",
    type: "text",
    group: "Labels",
  },
  {
    key: "closeSidebarLabel",
    label: "Close Sidebar Label",
    type: "text",
    group: "Labels",
  },
  {
    key: "newChatLabel",
    label: "New Chat Label",
    type: "text",
    group: "Labels",
  },
  {
    key: "chatsLabel",
    label: "Chats Label",
    type: "text",
    group: "Labels",
  },
  {
    key: "signOutLabel",
    label: "Sign Out Label",
    type: "text",
    group: "Labels",
  },
  {
    key: "labelFontSize",
    label: "Font Size",
    type: "number",
    min: 10,
    max: 18,
    step: 1,
    group: "Labels",
  },
  {
    key: "labelFontWeight",
    label: "Font Weight",
    type: "options",
    options: [...FONT_WEIGHT_OPTIONS_SHORT],
    group: "Labels",
  },
  { key: "labelColor", label: "Color", type: "color", group: "Labels" },

  // ── Tooltips ───────────────────────────────────────────────────────────
  {
    key: "tooltipBgColor",
    label: "Background",
    type: "color",
    group: "Tooltips",
  },
  {
    key: "tooltipTextColor",
    label: "Text Color",
    type: "color",
    group: "Tooltips",
  },
  {
    key: "tooltipFontSize",
    label: "Font Size",
    type: "number",
    min: 10,
    max: 16,
    step: 1,
    group: "Tooltips",
  },
  {
    key: "tooltipBorderRadius",
    label: "Border Radius",
    type: "number",
    min: 0,
    max: 16,
    step: 2,
    group: "Tooltips",
  },

  // ── Chat History ───────────────────────────────────────────────────────
  {
    key: "chatHistoryTitle",
    label: "Section Title",
    type: "text",
    group: "Chat History",
  },
  {
    key: "chatHistoryTitleFontSize",
    label: "Title Font Size",
    type: "number",
    min: 10,
    max: 18,
    step: 1,
    group: "Chat History",
  },
  {
    key: "chatHistoryTitleFontWeight",
    label: "Title Font Weight",
    type: "options",
    options: [...FONT_WEIGHT_OPTIONS_SHORT],
    group: "Chat History",
  },
  {
    key: "chatHistoryTitleColor",
    label: "Title Color",
    type: "color",
    group: "Chat History",
  },
  {
    key: "chatItemFontSize",
    label: "Item Font Size",
    type: "number",
    min: 10,
    max: 18,
    step: 1,
    group: "Chat History",
  },
  {
    key: "chatItemFontWeight",
    label: "Item Font Weight",
    type: "options",
    options: [...FONT_WEIGHT_OPTIONS_SHORT],
    group: "Chat History",
  },
  {
    key: "chatItemColor",
    label: "Item Text Color",
    type: "color",
    group: "Chat History",
  },
  {
    key: "chatItemActiveBgColor",
    label: "Active Item Background",
    type: "color",
    group: "Chat History",
  },
  {
    key: "chatItemHoverBgColor",
    label: "Hover Item Background",
    type: "color",
    group: "Chat History",
  },
  {
    key: "chatItemPaddingX",
    label: "Item Padding X",
    type: "number",
    min: 0,
    max: 24,
    step: 2,
    group: "Chat History",
  },
  {
    key: "chatItemPaddingY",
    label: "Item Padding Y",
    type: "number",
    min: 0,
    max: 16,
    step: 2,
    group: "Chat History",
  },
  {
    key: "chatItemBorderRadius",
    label: "Item Border Radius",
    type: "number",
    min: 0,
    max: 12,
    step: 1,
    group: "Chat History",
  },
  {
    key: "chatItemLineClamp",
    label: "Item Max Lines",
    type: "number",
    min: 1,
    max: 3,
    step: 1,
    group: "Chat History",
  },

  // ── Typography ─────────────────────────────────────────────────────────
  {
    key: "fontFamily",
    label: "Font Family",
    type: "font",
    group: "Typography",
  },

  // ── Avatar ─────────────────────────────────────────────────────────────
  {
    key: "avatarSize",
    label: "Size",
    type: "number",
    min: 16,
    max: 32,
    step: 2,
    group: "Avatar",
  },
  {
    key: "avatarBgColor",
    label: "Background",
    type: "color",
    group: "Avatar",
  },
  {
    key: "avatarTextColor",
    label: "Text Color",
    type: "color",
    group: "Avatar",
  },
  {
    key: "avatarFontSize",
    label: "Font Size",
    type: "number",
    min: 6,
    max: 14,
    step: 1,
    group: "Avatar",
  },
  {
    key: "avatarFontWeight",
    label: "Font Weight",
    type: "options",
    options: [...FONT_WEIGHT_OPTIONS_SHORT],
    group: "Avatar",
  },
];

// ---------------------------------------------------------------------------
// Widget component
// ---------------------------------------------------------------------------

export const ConciergeSidebarWidget: React.FC<ConciergeSidebarWidgetProps> = ({
  widgetId = "concierge-sidebar",
  displayName = "Concierge Sidebar",
  initialState = {},
  wrapperStyle,
  children,
}) => {
  const {
    state,
    isLoading,
    isSaving,
    handleEditChange,
    handleSave,
    themeColors,
    themeFonts,
    themeIcons,
    resolveColor,
    canSave,
  } = useWidgetEditable<ConciergeSidebarWidgetState>(
    widgetId,
    { ...defaultState, ...initialState },
    editFields,
  );

  // Resolve logo icon (same icon for both open & closed states)
  const logoCustomIcon =
    state.logoIcon && state.logoIcon !== "default"
      ? themeIcons[state.logoIcon]
      : null;

  const resolvedIcons: ConciergeSidebarResolvedIcons = {
    logoIconUrl: logoCustomIcon ? logoCustomIcon.url : null,
  };

  // Resolve font family
  const fontKey = state.fontFamily;
  const fontData =
    fontKey && fontKey !== "inherit" ? themeFonts[fontKey] : null;

  const googleFontLink =
    fontData && fontData.format === "google" ? fontData.url : null;

  useEffect(() => {
    if (googleFontLink) {
      loadGoogleFontInHead(googleFontLink);
    }
  }, [googleFontLink]);

  if (isLoading) {
    return <ConciergeSidebarSkeleton />;
  }

  const fontFamilyCss = fontData
    ? fontData.format === "system"
      ? fontData.url
      : `"${fontData.name}", sans-serif`
    : "inherit";

  const needsFontFace =
    fontData && fontData.format !== "system" && fontData.format !== "google";

  const fontFaceCss = needsFontFace
    ? `@font-face {
        font-family: "${fontData.name}";
        src: url("${fontData.url}") format("${fontData.format}");
        font-display: swap;
      }`
    : "";

  const enhancedState: ConciergeSidebarWidgetState & {
    fontFamilyCss?: string;
  } = {
    ...state,
    fontFamilyCss,
  };

  const widgetContent = (
    <>
      {fontFaceCss && <style>{fontFaceCss}</style>}
      {children(enhancedState, resolvedIcons, resolveColor)}
    </>
  );

  return (
    <WidgetWrapper
      widgetId={widgetId}
      displayName={displayName}
      editFields={editFields}
      editValues={state}
      onEditChange={handleEditChange}
      onSave={canSave ? handleSave : undefined}
      isSaving={isSaving}
      themeColors={themeColors}
      themeFonts={themeFonts}
      themeIcons={themeIcons}
      wrapperStyle={wrapperStyle}
    >
      {widgetContent}
    </WidgetWrapper>
  );
};
